import React, { useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Mail, Phone, MapPin } from 'lucide-react';
import { NAV_LINKS, SITE } from '../data/mock';

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F2E4]">
      {/* Header */}
      <header className="bg-[#0F1B2D] text-[#F8F2E4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="LawForLayman home">
            <img
              src={SITE.logoUrl}
              alt="LawForLayman seal"
              className="w-20 h-20 lg:w-28 lg:h-28 rounded-full object-cover"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === '/'}
                className={({ isActive }) =>
                  `nav-link text-[15px] tracking-wide font-sans-dm ${
                    isActive ? 'active text-[#F8F2E4]' : 'text-[#E8DCC0] hover:text-[#F8F2E4]'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <button
              aria-label="Cart"
              className="ml-2 p-2 rounded-full hover:bg-white/5 transition-colors"
              onClick={() => alert('Cart is empty (demo).')}
            >
              <ShoppingBag className="w-5 h-5" />
            </button>
          </nav>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/10">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === '/'}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `py-2 text-[15px] ${isActive ? 'text-[#E89530]' : 'text-[#E8DCC0]'}`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet key={location.pathname} />
      </main>

      {/* Footer */}
      <footer className="bg-[#0F1B2D] text-[#E8DCC0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <div className="w-14 h-1 bg-[#E89530] mb-6"></div>
              <h3 className="font-serif-besley text-3xl text-[#E89530] mb-2">{SITE.name}</h3>
              <p className="text-[11px] tracking-[0.15em] uppercase text-[#E8DCC0]/70 mb-4">
                A legal technology platform by<br />
                <span className="text-[#F8F2E4] tracking-[0.1em]">Adidar Tech India Private Limited</span>
              </p>
              <p className="text-[#C9BFA8] leading-relaxed max-w-xs">{SITE.tagline}</p>
            </div>

            <div>
              <h4 className="text-xs tracking-[0.25em] text-[#E89530] mb-5">PLATFORM</h4>
              <ul className="space-y-3 text-[15px]">
                <li><Link to="/" className="hover:text-[#F8F2E4] transition-colors">HOME</Link></li>
                <li><Link to="/find-a-lawyer" className="hover:text-[#F8F2E4] transition-colors">FIND A LAWYER</Link></li>
                <li><Link to="/legal-guides" className="hover:text-[#F8F2E4] transition-colors">GUIDES</Link></li>
                <li><Link to="/free-aid" className="hover:text-[#F8F2E4] transition-colors">FREE AID</Link></li>
                <li><Link to="/shop" className="hover:text-[#F8F2E4] transition-colors">SHOP</Link></li>
                <li><Link to="/track" className="hover:text-[#F8F2E4] transition-colors">TRACK REQUEST</Link></li>
                <li><Link to="/about" className="hover:text-[#F8F2E4] transition-colors">ABOUT</Link></li>
                <li><Link to="/get-help" className="hover:text-[#F8F2E4] transition-colors">GET HELP</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs tracking-[0.25em] text-[#E89530] mb-5">GET IN TOUCH</h4>
              <ul className="space-y-3 text-[15px]">
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 text-[#E89530] shrink-0" />
                  <a href={`mailto:${SITE.email}`} className="underline-offset-4 hover:underline">
                    {SITE.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-1 text-[#E89530] shrink-0" />
                  <span>Toll-Free Helpline: {SITE.helpline}</span>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-[#E89530] shrink-0" />
                  <span>{SITE.address}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-[#C9BFA8]">
            <div>
              © 2026 {SITE.name} · A legal technology platform developed and owned by <span className="text-[#F8F2E4]">Adidar Tech India Private Limited</span>.
            </div>
            <div className="tracking-[0.25em] text-[#E89530] text-xs">PLAIN LANGUAGE. VERIFIED ADVOCATES.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
